import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { find, isEmpty, throttle, uniqBy } from 'lodash';
import { usePreselectFiltersData } from '@/components/pages/SituationalAwareness/hooks/usePreselectFiltersData';
import { useCurrentOrganization } from '@/core/hooks/useCurrentOrganization';
import {
  AssetItem,
  IncidentItem,
  IncidentTypeItem,
} from '@/server/services/GoogleBigQuery/BigQueryIncidentsClient.types';
import { AssetItemTypes } from '@/server/services/GoogleBigQuery/BigQueryIncidentsClient.types.db';
import { MapDataDrawer } from '../MapView/MapDataDrawer';
import { RenderAssetsItem, RenderIncidentsItem } from '../MapView/MapDataDrawer/MapDataDrawer.types';
import { TargetAssetCard } from '@/components/composite/GeoMap/cards/TargetAssetCard';
import { TargetIncidentCard } from '@/components/composite/GeoMap/cards/TargetIncidentCard';
import { filterIncidents } from '../components/utils/filterIncidents';
import { useAssetsData } from '../hooks/useAssetsData';
import { useIncidentTypesData } from '../hooks/useIncidentTypesData';
import { useIncidentsData } from '../hooks/useIncidentsData';
import { useIncidentsDataFilters } from '../hooks/useIncidentsDataFilters';
import { useIncidentsDateTimeData } from '../hooks/useIncidentsDateTimeData';
import { useIncidentsStreaming } from '../hooks/useIncidentsStreaming';
import { useUserIncidentsColorSchemes } from '../hooks/useUserIncidentsColorSchemes';
import { useFiltersContext } from './FiltersContextProvider';
import { PanelType, useLeftSidePanelContext } from './LeftSidePanelContextProvider';
import { useMapContext } from './MapContextProvider';

export type ActiveTarget =
  | React.ComponentProps<typeof TargetAssetCard>['target']
  | React.ComponentProps<typeof TargetIncidentCard>['target'];

interface MapContextData {
  isLoadingData: boolean;
  incidentsData: IncidentItem[];
  incidentTypesData: IncidentTypeItem[];
  isGovernmentLevels: boolean;
  activeTarget: ActiveTarget | null;
  setActiveTarget(target: MapContextData['activeTarget']): void;
  activeIncidents: IncidentItem[] | null;
  setActiveIncidents(items: MapContextData['activeIncidents']): void;
  renderIncidents(incidents: IncidentItem[], args?: { withCentrify?: boolean }): void;
  setQueryIncidentsData: (items: IncidentItem[]) => void;
}

const MapEventsContext = React.createContext<MapContextData>({
  isLoadingData: false,
  incidentsData: [],
  incidentTypesData: [],
  isGovernmentLevels: false,
  renderIncidents: () => null,
  activeTarget: null,
  setActiveTarget: () => null,
  activeIncidents: null,
  setActiveIncidents: () => null,
  setQueryIncidentsData: () => [],
});

export const useMapEventsContext = () => React.useContext(MapEventsContext);

/**
 *  Load events, rendering and interaction
 */
export const MapEventsContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [activeTarget, setActiveTarget] = useState<MapContextData['activeTarget']>(null);
  const [activeIncidents, setActiveIncidents] = useState<IncidentItem[] | null>(null);
  const [queryIncidentsData, setQueryIncidentsData] = useState<IncidentItem[]>([]);
  const selectedAssets = useRef<RenderAssetsItem[]>([]);
  const { map } = useMapContext();
  const { organizationModel } = useCurrentOrganization();
  const { subscribe } = useIncidentsStreaming({ enabled: organizationModel?.isStreamingSupported() });
  const {
    setFilterFlags,
    hasFilters,
    filters,
    filterQuery,
    updateDefaultDateRangeFilters,
    applyFilters,
    setPreselectFilters,
  } = useFiltersContext();
  const { openPanel } = useLeftSidePanelContext();
  const { incidentTypesData } = useIncidentTypesData();
  const { assetsData } = useAssetsData();

  const { schemaKey, userSelectedColorMap } = useUserIncidentsColorSchemes();

  const colorScheme = useMemo(
    () => ({
      key: schemaKey,
      scheme: userSelectedColorMap,
    }),
    [userSelectedColorMap, schemaKey],
  ); // for compatibility purpose
  const setSelectedAssets = (assets: RenderAssetsItem[]) => (selectedAssets.current = assets);
  const getSelectedAssets = () => selectedAssets.current;
  const queryLimit = organizationModel?.hasIncidentQueryLimit() ? organizationModel.getIncidentQueryLimit() : undefined;

  const { incidentsDateTimeRange, isFetching: isFetchingDateTimeRange } = useIncidentsDateTimeData({
    enabled: !!queryLimit,
  });

  const { isPreSelectApplied } = usePreselectFiltersData({
    setPreselectFilters,
  });
  const incidentsQueryParams = useMemo(() => {
    if (!queryLimit) {
      return {
        enabled: isPreSelectApplied && !filterQuery,
      };
    }

    return {
      enabled: isPreSelectApplied && !!incidentsDateTimeRange.max && filters.dateRange.length > 0,
      limit: queryLimit,
      filters,
    };
  }, [queryLimit, filterQuery, incidentsDateTimeRange.max, filters, isPreSelectApplied]);

  const {
    incidentsData: pureIncidentsData,
    isFetching: isFetchingPureIncidents,
    refetch: refetchIncidents,
  } = useIncidentsData(incidentsQueryParams);

  const throttledRefetch = useMemo(
    () => throttle(() => refetchIncidents(), 1000, { leading: true, trailing: false }),
    [refetchIncidents],
  );

  const isGovernmentLevels = !!assetsData?.find((item) => item.AssetType === AssetItemTypes.GOVERNMENT);

  /** Reference to the current incidents data */
  const incidentsRef = useRef<IncidentItem[]>([]);

  const incidentsData = useMemo(() => {
    return filterQuery ? queryIncidentsData : pureIncidentsData;
  }, [filterQuery, pureIncidentsData, queryIncidentsData]);
  const extractedIncidentFilters = useIncidentsDataFilters(incidentsData);

  /**
   * Update reference to the current incidents data
   * This ensures every call to renderIncidentDonutClusters uses the latest incidentsData for onIncidentClick.
   */
  useEffect(() => {
    if (incidentsData) {
      incidentsRef.current = incidentsData;
    }
  }, [incidentsData]);

  useEffect(() => {
    updateDefaultDateRangeFilters({
      incidentsData: pureIncidentsData,
      incidentsDateTimeRange,
    });
  }, [pureIncidentsData, incidentsDateTimeRange]);

  useEffect(() => {
    if (!isEmpty(filterQuery) && !isEmpty(incidentsData)) {
      // Tech debt: this should be revisited any updated to a less hacky solution
      applyFilters(extractedIncidentFilters);
    }
  }, [filterQuery, incidentsData]);

  useEffect(() => {
    const unsubscribe = subscribe((message) => {
      if (message.type === 'pub_sub') {
        void throttledRefetch();
      }
    });

    return () => {
      unsubscribe();
      throttledRefetch.cancel();
    };
  }, [subscribe]);

  const mapDrawer = useMemo(() => (map ? new MapDataDrawer().setMap(map) : new MapDataDrawer()), [map]);

  const onAssetClick = (id: AssetItem['AssetID']) => {
    const item = find(assetsData!, { AssetID: id });
    if (item) {
      setActiveTarget(item);
    }
  };

  const onIncidentClick = (id: IncidentItem['IncidentID']) => {
    const item = find(incidentsRef.current, { IncidentID: id });
    if (item) {
      setActiveTarget(item);
    }
  };

  const onIncidentsClick = (ids: IncidentItem['IncidentID'][]) => {
    const items = incidentsRef.current.filter((item) => ids.includes(item.IncidentID));
    if (items.length) {
      setActiveIncidents(items);
    }
  };

  const renderIncidents: MapContextData['renderIncidents'] = useCallback(
    (incidents: IncidentItem[]) => {
      if (mapDrawer.hasMap()) {
        const items: RenderIncidentsItem[] =
          incidents.map((item) => ({
            id: item.IncidentID,
            title: item.Title,
            description: item.Title,
            latitude: item.Latitude,
            longitude: item.Longitude,
            IncidentTypeID: item.IncidentTypeID,
            SeverityLevel: item.SeverityLevel,
          })) || [];

        // README: Temporary disabled
        // const withCentrify = typeof args?.withCentrify !== 'undefined' ? args.withCentrify : hasFilters || !!filterQuery
        mapDrawer.renderIncidentDonutClusters({
          items,
          withCentrify: false,
          colorScheme: colorScheme,
          onClick: async (item, data) => {
            const ids = !isEmpty(data?.ids) ? data?.ids : [];
            if (ids.length > 1) {
              onIncidentsClick(ids);
              openPanel(PanelType.IncidentsFeed);
            } else {
              onIncidentClick(item.id);
            }
          },
          onClusterClick: async (clusterData, { ids }) => {
            onIncidentsClick(ids);
            openPanel(PanelType.IncidentsFeed);
          },
        });
      }
    },
    [mapDrawer, mapDrawer.hasMap(), hasFilters, filterQuery, colorScheme],
  );

  useEffect(() => {
    const toRenderAssetsItem = (item: AssetItem): RenderAssetsItem => ({
      id: item.AssetID,
      title: item.AssetName,
      icon: item.Icon,
      description: item.AssetDescription,
      impactRadius: item.ImpactRadius,
      latitude: item.Latitude,
      longitude: item.Longitude,
    });
    if (mapDrawer.hasMap() && !isEmpty(assetsData)) {
      const items: RenderAssetsItem[] = assetsData?.map((item) => toRenderAssetsItem(item)) || [];

      mapDrawer.renderAssets({
        items,
        onClick: (item, { metaKey }) => {
          if (metaKey) {
            const asset = assetsData?.find(({ AssetID }) => AssetID === item.id);
            const selectedAssets = getSelectedAssets();
            const isAlreadySelected = selectedAssets.find((i) => i.id === item.id);
            let updatedSelectedAssets = asset
              ? uniqBy([...getSelectedAssets(), toRenderAssetsItem(asset)], 'id')
              : getSelectedAssets();

            if (isAlreadySelected) {
              updatedSelectedAssets = updatedSelectedAssets.filter((i) => i.id !== item.id);
            }
            setSelectedAssets(updatedSelectedAssets);
            mapDrawer.renderSelectedAssetProximity(updatedSelectedAssets);
          } else {
            onAssetClick(item.id as string);
          }
        },
      });
    }
  }, [mapDrawer, mapDrawer.hasMap(), assetsData]);

  useEffect(() => {
    if (!isEmpty(incidentsData)) {
      // README: Temporary rendering via demo controls
      if (organizationModel?.isDemoControlsAvailable()) {
        // README: Rendering are via demo controls
      } else {
        const incidents =
          // Avoid client filtering if limit is applied
          hasFilters && !organizationModel?.hasIncidentQueryLimit()
            ? filterIncidents(incidentsData!, filters)
            : incidentsData;
        renderIncidents(incidents);
      }
    } else {
      // just render empty
      renderIncidents([]);
    }
  }, [mapDrawer, incidentsData, hasFilters, filters, renderIncidents, organizationModel, schemaKey]);

  // useEffect(() => {
  //   if (queryLimit) {
  //     // Re-fetch incidents with applied filters and limit
  //     throttledRefetch();
  //   }
  // }, [filters]);

  useEffect(() => {
    if (!activeTarget) {
      mapDrawer.resetActiveTargets();
    }
  }, [mapDrawer, activeTarget]);

  useEffect(() => {
    if (!isEmpty(incidentsData)) {
      const verificationSourceFieldAvailable = incidentsData.some((item) => !!item.VerificationSource);
      const industryFieldAvailable = incidentsData.some((item) => !!item.Industry);
      setFilterFlags?.((flags) => ({
        ...flags,
        verificationSources: verificationSourceFieldAvailable,
        industries: industryFieldAvailable,
      }));
    }
  }, [incidentsData]);

  return (
    <MapEventsContext.Provider
      value={{
        activeTarget,
        incidentsData: incidentsData || [],
        incidentTypesData: incidentTypesData || [],
        isLoadingData: isFetchingPureIncidents || isFetchingDateTimeRange,
        isGovernmentLevels,
        setActiveTarget,
        activeIncidents,
        setActiveIncidents,
        renderIncidents,
        setQueryIncidentsData,
      }}
    >
      {children}
    </MapEventsContext.Provider>
  );
};
